import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Check, RotateCcw, Trash2, Pencil, AlertCircle, RefreshCcw, Megaphone, FileText, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { value: 'error', label: 'Error', icon: AlertCircle, color: 'red', bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  { value: 'refund', label: 'Refund Due', icon: RefreshCcw, color: 'amber', bg: 'bg-amber-50', border: 'border-amber-300', badge: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  { value: 'update', label: 'Store Update', icon: Megaphone, color: 'blue', bg: 'bg-blue-50', border: 'border-blue-300', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { value: 'other', label: 'Other', icon: FileText, color: 'slate', bg: 'bg-slate-50', border: 'border-slate-300', badge: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
];

const catMeta = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[3];

export default function NoticeBoard() {
  const { token } = useAuth();
  const { selectedStore, storeName } = useStore();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [text, setText] = useState('');
  const [category, setCategory] = useState('error');
  const [targetStore, setTargetStore] = useState('this');  // this | other | both

  // Edit dialog state
  const [editNote, setEditNote] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('error');
  const [editSaving, setEditSaving] = useState(false);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  const storeKey = (selectedStore || 'orewa').toLowerCase();

  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${API_URL}/notice-board?store=${storeKey}`, { headers: getAuthHeader() });
      setNotes(res.data || []);
    } catch (e) {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchNotes();
    // Mark as seen when this page is opened (clears ding badge)
    axios.post(`${API_URL}/notice-board/mark-seen`, { store: storeKey }, { headers: getAuthHeader() }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey]);

  const handleCreate = async () => {
    if (!text.trim()) {
      toast.error('Please write a note first');
      return;
    }
    setSaving(true);
    try {
      const otherStore = storeKey === 'orewa' ? 'eastridge' : 'orewa';
      const target = targetStore === 'this' ? storeKey : targetStore === 'other' ? otherStore : 'both';
      await axios.post(`${API_URL}/notice-board`, {
        text: text.trim(),
        category,
        store: storeKey,
        target_store: target,
      }, { headers: getAuthHeader() });
      toast.success(
        target === 'both' ? 'Note posted to BOTH stores' :
        target === storeKey ? `Note posted to ${storeKey}` :
        `Note posted to ${target} (other store will hear a ding)`
      );
      setText('');
      setCategory('error');
      setTargetStore('this');
      setShowCreate(false);
      fetchNotes();
    } catch (e) {
      toast.error('Failed to post note');
    } finally {
      setSaving(false);
    }
  };

  const toggleResolved = async (note) => {
    try {
      await axios.put(`${API_URL}/notice-board/${note.id}`, { resolved: !note.resolved }, { headers: getAuthHeader() });
      fetchNotes();
    } catch (e) {
      toast.error('Failed to update note');
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API_URL}/notice-board/${id}`, { headers: getAuthHeader() });
      fetchNotes();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (note) => {
    setEditNote(note);
    setEditText(note.text || '');
    setEditCategory(note.category || 'other');
  };

  const saveEdit = async () => {
    if (!editNote) return;
    if (!editText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/notice-board/${editNote.id}`, {
        text: editText.trim(),
        category: editCategory,
      }, { headers: getAuthHeader() });
      toast.success('Note updated');
      setEditNote(null);
      fetchNotes();
    } catch (e) {
      toast.error('Failed to update note');
    } finally {
      setEditSaving(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
  };

  const activeNotes = notes.filter(n => !n.resolved);
  const resolvedNotes = notes.filter(n => n.resolved);

  return (
    <div className="space-y-6" data-testid="notice-board-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Notice Board — {storeName(storeKey)}
          </h1>
          <p className="text-slate-500 mt-1">Post notes for staff: errors, refunds due, store updates, anything important.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="nb-new-note">
          <Plus className="w-4 h-4 mr-2" /> New Note
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Active section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="font-bold text-slate-700 uppercase tracking-wider text-sm">Active</h2>
              <Badge variant="outline" className="text-xs">{activeNotes.length}</Badge>
            </div>
            {activeNotes.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-10 text-center text-slate-400 text-sm">
                  Nothing on the board. Click "New Note" to post something.
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {activeNotes.map((n) => {
                  const meta = catMeta(n.category);
                  const Icon = meta.icon;
                  return (
                    <Card key={n.id} className={`border-l-4 ${meta.border} ${meta.bg}`} data-testid={`nb-note-${n.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 text-${meta.color}-600`} />
                            <Badge className={`${meta.badge} text-xs`}>{meta.label}</Badge>
                          </div>
                          <span className="text-xs text-slate-400">{formatTime(n.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xs text-slate-500">— {n.author_name || 'Staff'}</span>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                              onClick={() => openEdit(n)} data-testid={`nb-edit-${n.id}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-green-400 text-green-700 hover:bg-green-50"
                              onClick={() => toggleResolved(n)} data-testid={`nb-resolve-${n.id}`}>
                              <Check className="w-3 h-3 mr-1" /> Resolve
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-400 hover:text-red-600"
                              onClick={() => deleteNote(n.id)} data-testid={`nb-delete-${n.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>

          {/* Resolved section */}
          {resolvedNotes.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3 mt-8">
                <h2 className="font-bold text-slate-400 uppercase tracking-wider text-sm">Resolved</h2>
                <Badge variant="outline" className="text-xs">{resolvedNotes.length}</Badge>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {resolvedNotes.map((n) => {
                  const meta = catMeta(n.category);
                  return (
                    <Card key={n.id} className="border-slate-200 opacity-70 hover:opacity-100 transition-opacity">
                      <CardContent className="p-3 flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                            <Badge className={`${meta.badge} text-[10px]`}>{meta.label}</Badge>
                            <span className="text-xs text-slate-400">{formatTime(n.resolved_at || n.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-600 line-through truncate">{n.text}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
                            onClick={() => toggleResolved(n)} data-testid={`nb-reopen-${n.id}`}>
                            <RotateCcw className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-400 hover:text-red-600"
                            onClick={() => deleteNote(n.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">New Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Category</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const selected = category === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCategory(c.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                        selected ? `${c.bg} ${c.border} text-${c.color}-800` : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`nb-cat-${c.value}`}
                    >
                      <Icon className={`w-4 h-4 text-${c.color}-600`} />
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's happening? E.g. 'Refund of $45 owed to Sarah - paid card declined twice'"
                rows={5}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none mt-1"
                data-testid="nb-text"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Send to</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { key: 'this', label: `This store (${storeName(storeKey)})`, color: 'border-slate-300' },
                  { key: 'other', label: `Other store (${storeName(storeKey === 'orewa' ? 'eastridge' : 'orewa')}) ` , color: 'border-amber-400 text-amber-700' },
                  { key: 'both', label: 'Both stores', color: 'border-emerald-400 text-emerald-700' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setTargetStore(opt.key)}
                    className={`px-3 py-2 rounded-md text-xs font-semibold border-2 transition-colors ${
                      targetStore === opt.key
                        ? (opt.key === 'this' ? 'bg-slate-900 text-white border-slate-900'
                          : opt.key === 'other' ? 'bg-amber-100 border-amber-500 text-amber-900'
                          : 'bg-emerald-100 border-emerald-500 text-emerald-900')
                        : `bg-white ${opt.color}`
                    }`}
                    data-testid={`nb-target-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {targetStore !== 'this' && (
                <p className="text-xs text-amber-700 mt-1">The other store will hear a ding when this is posted.</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="nb-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Post Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editNote} onOpenChange={(open) => { if (!open) setEditNote(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Note</DialogTitle>
          </DialogHeader>
          {editNote && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const selected = editCategory === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setEditCategory(c.value)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                          selected ? `${c.bg} ${c.border} text-${c.color}-800` : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`nb-edit-cat-${c.value}`}
                      >
                        <Icon className={`w-4 h-4 text-${c.color}-600`} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Note</label>
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={5}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none mt-1"
                  data-testid="nb-edit-text"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setEditNote(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving} className="bg-slate-900 hover:bg-slate-800" data-testid="nb-edit-submit">
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
